import { useState, useRef, useEffect } from "react";
import { db, storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection, doc, getDoc, updateDoc, getDocs,
  query, where, onSnapshot,
} from "firebase/firestore";
import { Avatar, IC, Stars, ThemeToggle, VerifiedBadge, useToast } from "../shared";
import EditProfile from "./EditProfile";
import ForgotItem from "./ForgotItem";
import { ReviewCard, BreakdownRow } from "./AllReviews";

// ── Profile Screen ────────────────────────────────────────────────────────────
const Profile = ({ currentUser, onLogout, onAvatarUpdate, onPhoneUpdate, onNavigate }) => {
  const toast = useToast();
  const [uploadingPic,    setUploadingPic]    = useState(false);
  const [showEdit,        setShowEdit]        = useState(false);
  const [showReviews,     setShowReviews]     = useState(false);
  const [showForgotItem,  setShowForgotItem]  = useState(false);
  const [tripCount, setTripCount] = useState(0);
  const fileInputRef = useRef(null);

  // Count completed trips (as driver + accepted as passenger)
  useEffect(() => {
    const q1 = query(collection(db, "trips"), where("driverId", "==", currentUser.id));
    const q2 = query(collection(db, "requests"), where("passengerId", "==", currentUser.id), where("status", "==", "accepted"));
    const unsub1 = onSnapshot(q1, snap => {
      const driverCount = snap.docs.filter(d => new Date(d.data().datetime) <= new Date()).length;
      setTripCount(prev => driverCount + (prev - Math.floor(prev / 2))); // rough update
    }, err => console.warn("Trip count listener error suppressed:", err.message));
    const unsub2 = onSnapshot(q2, snap => {
      const passengerCount = snap.docs.filter(d => new Date(d.data().tripDatetime) <= new Date()).length;
      // Combine: query both and set total
      getDocs(query(collection(db, "trips"), where("driverId", "==", currentUser.id))).then(dSnap => {
        const dc = dSnap.docs.filter(d => new Date(d.data().datetime) <= new Date()).length;
        setTripCount(dc + passengerCount);
      });
    }, err => console.warn("Request count listener error suppressed:", err.message));
    return () => { unsub1(); unsub2(); };
  }, [currentUser.id]);

  const resizeImage = (file, size = 400) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      const min = Math.min(img.width, img.height);
      const sx = (img.width  - min) / 2;
      const sy = (img.height - min) / 2;
      ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Resize failed")), "image/jpeg", 0.88);
    };
    img.onerror = reject;
    img.src = url;
  });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const resized = await resizeImage(file, 400);
      const storageRef = ref(storage, `avatars/${currentUser.id}`);
      await uploadBytes(storageRef, resized);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "users", currentUser.id), { avatar: url });
      // Update UI immediately after user doc is saved
      onAvatarUpdate?.(url);
      toast("Profile picture updated!", "success");
      // Cascade avatar update to trips & requests in background (non-blocking)
      try {
        const [tripSnap, reqAsDriverSnap, reqAsPassSnap] = await Promise.all([
          getDocs(query(collection(db, "trips"),    where("driverId",    "==", currentUser.id))),
          getDocs(query(collection(db, "requests"), where("driverId",    "==", currentUser.id))),
          getDocs(query(collection(db, "requests"), where("passengerId", "==", currentUser.id))),
        ]);
        await Promise.all([
          ...tripSnap.docs.map(d        => updateDoc(doc(db, "trips",    d.id), { driverAvatar:    url })),
          ...reqAsDriverSnap.docs.map(d => updateDoc(doc(db, "requests", d.id), { driverAvatar:    url })),
          ...reqAsPassSnap.docs.map(d   => updateDoc(doc(db, "requests", d.id), { passengerAvatar: url })),
        ]);
      } catch (cascadeErr) {
        console.warn("Avatar cascade update suppressed:", cascadeErr.message);
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
      if (err.code === "storage/unauthorized" || /permission/i.test(err.message)) {
        console.warn("Avatar permission error suppressed:", err.message);
      } else {
        const reason = err.code === "storage/canceled" ? "Upload was cancelled."
          : err.code === "storage/retry-limit-exceeded" ? "Upload failed — slow connection. Please try again."
          : "Couldn't upload photo. Please try again.";
        toast(reason, "error");
      }
    } finally {
      setUploadingPic(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const memberSince = new Date(currentUser.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString("en", { month: "short", year: "numeric" });

  // ── Edit Profile sub-screen ──
  if (showEdit) {
    return <EditProfile currentUser={currentUser} onBack={() => setShowEdit(false)} onPhoneUpdate={onPhoneUpdate}/>;
  }

  // ── Forgot an Item sub-screen ──
  if (showForgotItem) {
    return <ForgotItem currentUser={currentUser} onBack={() => setShowForgotItem(false)} onOpenLostChat={(chat) => { setShowForgotItem(false); onNavigate?.("chat", chat); }}/>;
  }

  // ── My Reviews sub-screen ──
  if (showReviews) {
    return <MyReviews currentUser={currentUser} onBack={() => setShowReviews(false)}/>;
  }

  return (
  <div className="screen" style={{ padding: "0 0 90px", overflowY: "auto" }}>
  <div className="desk-container-sm" style={{ padding: 0 }}>

    {/* ── Premium Header ── */}
    <div className="igo-profile-header">
      <div className="igo-profile-banner">
        <div className="igo-profile-avatar-wrap">
          <div style={{ position: "relative", cursor: "pointer", width: 80, height: 80 }} onClick={() => fileInputRef.current?.click()}>
            <div style={{ borderRadius: "50%", border: "4px solid var(--card)", overflow: "hidden", width: 88, height: 88, boxSizing: "border-box", boxShadow: "var(--shadow-lg)" }}>
              <Avatar user={currentUser} size={80} tappable={false}/>
            </div>
            {(currentUser.rating || 0) >= 4.5 ? (
              <div style={{ position: "absolute", bottom: 2, right: 2, width: 28, height: 28, borderRadius: "50%", background: "var(--accent)", border: "2px solid var(--card)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.15)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
              </div>
            ) : (
              <div style={{ position: "absolute", bottom: 2, right: 2, width: 28, height: 28, borderRadius: "50%", background: "var(--accent)", border: "2px solid var(--card)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.15)", transition: "transform .15s" }} onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                {uploadingPic
                  ? <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                }
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoChange}/>
          </div>
        </div>
      </div>
      <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        {currentUser.name}
        {(currentUser.rating || 0) >= 4.5 && <VerifiedBadge size={16}/>}
      </div>
      <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{currentUser.email}</div>
    </div>

    {/* ── Stats Cards ── */}
    <div className="igo-profile-stats aslide-fast">
      <div className="igo-profile-stat">
        <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--accent)", fontVariantNumeric: "tabular-nums" }}>{tripCount}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>Trips</div>
      </div>
      <div className="igo-profile-stat">
        <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--accent)", fontVariantNumeric: "tabular-nums" }}>{currentUser.rating ? currentUser.rating.toFixed(1) : "—"}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>Rating</div>
      </div>
      <div className="igo-profile-stat">
        <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--accent)" }}>{memberSince.split(" ")[0]}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>Since {memberSince.split(" ")[1]}</div>
      </div>
    </div>

    {/* ── Settings Items ── */}
    <div style={{ padding: "0 20px", marginBottom: 16 }} className="aslide">
      <div className="igo-profile-item" onClick={() => setShowEdit(true)}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(43,91,224,.1)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ width: 18, height: 18 }}>{IC.user}</span>
        </div>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>Edit Profile</span>
        <svg style={{ width: 16, height: 16, color: "var(--muted)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
      </div>

      <div className="igo-profile-item" onClick={() => setShowReviews(true)}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(245,166,35,.1)", color: "#f5a623", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ width: 18, height: 18 }}>{IC.star}</span>
        </div>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>My Reviews</span>
        <svg style={{ width: 16, height: 16, color: "var(--muted)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
      </div>

      <div className="igo-profile-item" onClick={() => setShowForgotItem(true)}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(245,166,35,.1)", color: "#f5a623", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
        </div>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>Forgot an Item</span>
        <svg style={{ width: 16, height: 16, color: "var(--muted)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
      </div>

      <div className="igo-profile-item" style={{ cursor: "default" }}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(90,128,240,.1)", color: "#5a80f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
        </div>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>Dark Mode</span>
        <ThemeToggle/>
      </div>
    </div>

    {/* ── Log Out ── */}
    <div style={{ padding: "0 20px", marginBottom: 24 }}>
      <div className="igo-profile-item" onClick={onLogout} style={{ borderColor: "rgba(192,57,43,.15)" }}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(192,57,43,.08)", color: "#c0392b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ width: 18, height: 18 }}>{IC.logout}</span>
        </div>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: "#c0392b" }}>Log Out</span>
      </div>
    </div>

  </div></div>
  );
};

// ── My Reviews Sub-screen ────────────────────────────────────────────────────
const starFilterIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;

const MyReviews = ({ currentUser, onBack }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starFilter, setStarFilter] = useState(0);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "ratings"), where("ratedId", "==", currentUser.id)));
      const items = [];
      for (const d of snap.docs) {
        const r = d.data();
        const userSnap = await getDoc(doc(db, "users", r.raterId));
        const rater = userSnap.exists() ? userSnap.data() : null;
        let role = r.ratedAs || null;
        if (!role && r.tripId) {
          const tripSnap = await getDoc(doc(db, "trips", r.tripId));
          if (tripSnap.exists()) {
            role = tripSnap.data().driverId === currentUser.id ? "driver" : "passenger";
          }
        }
        items.push({ id: d.id, score: r.score, comment: r.comment || null, ratedAs: role, ratedAt: r.ratedAt, tripId: r.tripId, raterName: rater?.name || "Unknown", raterAvatar: rater?.avatar || null, raterId: r.raterId });
      }
      items.sort((a, b) => (b.ratedAt?.seconds || 0) - (a.ratedAt?.seconds || 0));
      setReviews(items);
      setLoading(false);
    })();
  }, [currentUser.id]);

  const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach(r => { if (counts[r.score] !== undefined) counts[r.score]++; });
  const maxCount = Math.max(...Object.values(counts), 1);
  const filtered = starFilter === 0 ? reviews : reviews.filter(r => r.score === starFilter);

  return (
    <div className="screen" style={{ padding: "0 0 90px", overflowY: "auto" }}>
      <div className="desk-container-sm" style={{ padding: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 20px 0" }}>
          <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 12, background: "var(--warm)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <span style={{ width: 18, height: 18, color: "var(--ink)" }}>{IC.back}</span>
          </button>
          <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", margin: 0, flex: 1 }}>My Reviews</h2>
          {reviews.length > 0 && (
            <span style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", background: "rgba(43,91,224,.08)", padding: "3px 10px", borderRadius: 99, border: "1px solid rgba(43,91,224,.15)" }}>{reviews.length}</span>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
            <span style={{ width: 24, height: 24, border: "3px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
            <div style={{ fontSize: 40 }}>⭐</div>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, marginTop: 10 }}>No reviews yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Ratings from your trips will appear here</div>
          </div>
        ) : (
          <>
            {/* Summary with breakdown */}
            <div style={{ padding: "16px 20px 0" }}>
              <div style={{ display: "flex", gap: 20, alignItems: "center", padding: 16, background: "var(--card)", borderRadius: 18, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 32, color: "var(--accent)" }}>{currentUser.rating ? currentUser.rating.toFixed(1) : "—"}</div>
                  <Stars rating={currentUser.rating || 0} size={14}/>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  {[5, 4, 3, 2, 1].map(s => (
                    <BreakdownRow key={s} star={s} count={counts[s]} max={maxCount}/>
                  ))}
                </div>
              </div>
            </div>

            {/* Star filter pills */}
            <div style={{ display: "flex", gap: 6, padding: "14px 20px 8px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {[{ label: "All", val: 0 }, ...([5,4,3,2,1].map(n => ({ label: n, val: n })))].map(f => {
                const active = starFilter === f.val;
                return (
                  <button key={f.val} onClick={() => setStarFilter(f.val)} style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "7px 14px", borderRadius: 99, flexShrink: 0,
                    border: active ? "1.5px solid var(--accent)" : "1.5px solid var(--border)",
                    background: active ? "var(--accent)" : "var(--card)",
                    fontFamily: "Syne", fontWeight: 700, fontSize: 12,
                    color: active ? "#fff" : "var(--muted)",
                    cursor: "pointer", transition: "all .15s",
                  }}>
                    {f.val > 0 && <span style={{ width: 12, height: 12, color: active ? "#fff" : "#f5a623" }}>{starFilterIcon}</span>}
                    {f.label}
                  </button>
                );
              })}
            </div>

            {/* Filter count */}
            <div style={{ padding: "0 20px 8px", fontSize: 12, color: "var(--muted)", fontFamily: "Syne", fontWeight: 600 }}>
              {starFilter === 0 ? `Showing all ${reviews.length} reviews` : `Showing ${filtered.length} review${filtered.length !== 1 ? "s" : ""} with ${starFilter} star${starFilter !== 1 ? "s" : ""}`}
            </div>

            {/* Review list */}
            <div style={{ padding: "0 20px" }}>
              {filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>
                    <span style={{ width: 36, height: 36, color: "#ddd", display: "inline-block" }}>{starFilterIcon}</span>
                  </div>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15 }}>No {starFilter}-star reviews</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>You haven't received any {starFilter}-star ratings yet</div>
                </div>
              ) : (
                filtered.map(r => (
                  <ReviewCard key={r.id} r={r} cardStyle={{
                    background: "var(--card)", borderRadius: 16, padding: "14px 16px",
                    border: "1.5px solid var(--border)", marginBottom: 10, boxShadow: "var(--shadow)",
                  }}/>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
